// iter-121.118 — EA Cockpit Scope Context
//
// Sibling of `oa-scope.jsx`. EA tenant is always pinned server-side, so
// the context only exposes Workspace / User / Dataset filters.
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";

const EaScopeContext = createContext(null);

const DEFAULT_SCOPE = {
    period: "30d",
    custom_start: null,
    custom_end: null,
    workspace_id: null,
    owner_id: null,
    dataset_id: null,
};

export function EaScopeProvider({ children }) {
    const [scope, setScope] = useState(DEFAULT_SCOPE);
    const [loaded, setLoaded] = useState(false);
    const [saving, setSaving] = useState(false);
    const [options, setOptions] = useState({ workspaces: [], owners: [], datasets: [] });
    const [optionsLoading, setOptionsLoading] = useState(false);

    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                const r = await api.get("/home/ea/scope/preferences");
                if (!alive) return;
                const d = r.data || {};
                setScope({
                    period: d.period || "30d",
                    custom_start: d.custom_start || null,
                    custom_end: d.custom_end || null,
                    workspace_id: d.workspace_id || null,
                    owner_id: d.owner_id || null,
                    dataset_id: d.dataset_id || null,
                });
            } catch {
                // 403 or 5xx — fall back to defaults silently.
            } finally {
                if (alive) setLoaded(true);
            }
        })();
        return () => { alive = false; };
    }, []);

    useEffect(() => {
        if (!loaded) return;
        (async () => {
            setOptionsLoading(true);
            try {
                const params = new URLSearchParams({ period: scope.period });
                if (scope.custom_start) params.set("custom_start", scope.custom_start);
                if (scope.custom_end)   params.set("custom_end",   scope.custom_end);
                if (scope.workspace_id) params.set("workspace_id", scope.workspace_id);
                const r = await api.get(`/home/ea/scope/options?${params}`);
                setOptions({
                    workspaces: r.data?.workspaces || [],
                    owners: r.data?.owners || [],
                    datasets: r.data?.datasets || [],
                });
            } catch {
                setOptions({ workspaces: [], owners: [], datasets: [] });
            } finally {
                setOptionsLoading(false);
            }
        })();
    }, [scope.period, scope.custom_start, scope.custom_end, scope.workspace_id, loaded]);

    const setPeriod = useCallback((period) => {
        setScope((s) => ({
            ...s,
            period: period || "30d",
            custom_start: period === "custom" ? s.custom_start : null,
            custom_end:   period === "custom" ? s.custom_end   : null,
        }));
    }, []);

    const setCustomRange = useCallback((start, end) => {
        setScope((s) => ({
            ...s, period: "custom",
            custom_start: start || null, custom_end: end || null,
        }));
    }, []);

    const setWorkspaceId = useCallback((workspace_id) => {
        // Switching workspace clears dataset (may have belonged to old ws).
        setScope((s) => ({
            ...s, workspace_id: workspace_id || null, dataset_id: null,
        }));
    }, []);

    const setOwnerId = useCallback((owner_id) => {
        setScope((s) => ({ ...s, owner_id: owner_id || null }));
    }, []);

    const setDatasetId = useCallback((dataset_id) => {
        setScope((s) => ({ ...s, dataset_id: dataset_id || null }));
    }, []);

    const reset = useCallback(() => setScope(DEFAULT_SCOPE), []);

    const saveAsDefault = useCallback(async () => {
        setSaving(true);
        try {
            await api.put("/home/ea/scope/preferences", scope);
            return true;
        } catch {
            return false;
        } finally {
            setSaving(false);
        }
    }, [scope]);

    const queryString = useMemo(() => {
        const p = new URLSearchParams({ period: scope.period });
        if (scope.custom_start) p.set("custom_start", scope.custom_start);
        if (scope.custom_end)   p.set("custom_end",   scope.custom_end);
        if (scope.workspace_id) p.set("workspace_id", scope.workspace_id);
        if (scope.owner_id)     p.set("owner_id",     scope.owner_id);
        if (scope.dataset_id)   p.set("dataset_id",   scope.dataset_id);
        return p.toString();
    }, [scope]);

    const value = useMemo(() => ({
        scope, options, optionsLoading, loaded, saving,
        setPeriod, setCustomRange, setWorkspaceId, setOwnerId, setDatasetId,
        reset, saveAsDefault, queryString,
    }), [scope, options, optionsLoading, loaded, saving,
        setPeriod, setCustomRange, setWorkspaceId, setOwnerId, setDatasetId,
        reset, saveAsDefault, queryString]);

    return (
        <EaScopeContext.Provider value={value}>
            {children}
        </EaScopeContext.Provider>
    );
}

export function useEaScope() {
    const ctx = useContext(EaScopeContext);
    if (!ctx) throw new Error("useEaScope must be used inside <EaScopeProvider>");
    return ctx;
}
